/**
 * Procurar no histórico de um servidor.
 *
 * Atravessa canais — é a única vista da app que o faz — por isso cada linha tem
 * de dizer DE ONDE veio. Sem o nome do canal ao lado, dois resultados iguais de
 * sítios diferentes são indistinguíveis, e carregar num deles leva a pessoa para
 * um sítio onde ela não percebe que está.
 *
 * A FORMA é a do Discord, e a mudança foi pedida assim: *"essa parte tá com
 * muita cara de IA, deixe igual ao do discord"*. Era um modal ao meio do ecrã —
 * título, caixa grande, dois `<select>` etiquetados, um parágrafo de ajuda — e
 * um formulário centrado é exactamente o que nenhuma app de conversa faz. As
 * três decisões que mudam isso:
 *
 *  1. os RESULTADOS ocupam a coluna da direita, no lugar da lista de membros.
 *     São mensagens inteiras vindas de canais diferentes: precisam de largura e
 *     de altura, e nenhuma das duas cabe num diálogo por cima da conversa. E ao
 *     ficarem AO LADO, continua a ver-se o canal por trás — carregar num
 *     resultado deixa de ser um salto às escuras;
 *  2. a CAIXA é o cabeçalho do PAINEL — *"deixa a caixa de escrever junto ali no
 *     canto direito"*. Esteve na barra do canal, a meio do ecrã, e ficava órfã:
 *     longe da coluna que ela enche, e a repetir o que a coluna já dizia (um
 *     título «PROCURAR» sobre uma caixa que diz «Procurar», e dois ✕ para a
 *     mesma acção). Junta, é uma coisa só — e a contagem, que é o que vale a
 *     pena ler ali, passa a ser a etiqueta da lista;
 *  3. os filtros ESCREVEM-SE (`de:` e `em:`) em vez de se escolherem em dois
 *     selectores — ver `separarFiltros`, em `lib/procura`.
 *
 * O que NÃO mudou: o anti-corrida dos pedidos, a espera entre teclas, o mínimo
 * de letras e a paginação. Isso era o miolo e estava certo.
 *
 * Não se decide nada sobre permissões aqui: a lista de canais onde se procura é
 * a que o SERVIDOR resolve a partir de quem pergunta.
 */

import type { Message, Snowflake } from "@greenvoice/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { formatMessageStamp } from "../lib/format";
import { excerto, nomeCombina, realcar, separarFiltros, termosDe } from "../lib/procura";
import { channelsOf, isTextual } from "../store/selectors";
import { useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { IconClose, IconSearch } from "./Icons";

/**
 * Quanto se espera depois da última tecla antes de perguntar ao servidor.
 *
 * Cada pedido é uma full-text search a sério; disparar a cada tecla dava sete
 * consultas para escrever "reunião" e seis eram deitadas fora. É também o que
 * impede a caixa de bater no limite de pedidos a meio de uma palavra escrita
 * depressa.
 */
const ESPERA_MS = 350;
/** Abaixo disto quase tudo casa e a resposta não diz nada. */
const MINIMO = 2;

export function MessageSearch({ serverId, onClose }: { serverId: Snowflake; onClose: () => void }) {
  const state = useStore();

  /** O que está na caixa, com filtros e tudo: `orçamento de:kawã em:geral`. */
  const [entrada, setEntrada] = useState("");

  const [resultados, setResultados] = useState<Message[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** Já houve uma procura com resposta? Distingue "ainda não escreveu nada" de
   *  "escreveu e não há nada" — que é a diferença entre uma dica e um vazio. */
  const [respondeu, setRespondeu] = useState(false);

  /**
   * Do que se escreveu para o que se pergunta ao servidor.
   *
   * Os nomes resolvem-se para ids AQUI e não no servidor: a API recebe
   * `authorId`/`channelId`, e é o cliente que tem a lista de membros e de canais
   * à mão. Um nome que não corresponda a ninguém fica `undefined` — procura-se
   * sem esse filtro em vez de devolver vazio, porque a pessoa está a MEIO de
   * escrever o nome e uma lista que pisca a cada letra é pior do que uma lista
   * larga de mais.
   */
  const { texto, de, em } = useMemo(() => separarFiltros(entrada), [entrada]);
  const canais = useMemo(() => channelsOf(state, serverId).filter(isTextual), [state, serverId]);
  const canalId = useMemo(() => (em ? canais.find((c) => nomeCombina(c.name, em))?.id : undefined), [canais, em]);
  const autorId = useMemo(() => {
    if (!de) return undefined;
    const membro = Object.values(state.members).find(
      (m) => m.serverId === serverId && nomeCombina(m.nickname ?? m.user.displayName ?? m.user.username, de),
    );
    return membro?.user.id;
  }, [state.members, serverId, de]);

  /**
   * O número do pedido em curso. Sem ele, a resposta lenta de "reuni" chegava
   * depois da de "reunião" e escrevia por cima do resultado certo — o clássico
   * das caixas de procura, e dos mais difíceis de reproduzir de propósito.
   */
  const pedido = useRef(0);

  useEffect(() => {
    const limpo = texto.trim();
    if (limpo.length < MINIMO) {
      pedido.current++;
      setResultados([]);
      setHasMore(false);
      setBusy(false);
      setErro(null);
      setRespondeu(false);
      return;
    }

    const meu = ++pedido.current;
    setBusy(true);
    setErro(null);

    const timer = window.setTimeout(() => {
      api.servers
        .searchMessages(serverId, {
          q: limpo,
          ...(canalId ? { channelId: canalId } : {}),
          ...(autorId ? { authorId: autorId } : {}),
        })
        .then((page) => {
          if (pedido.current !== meu) return;
          setResultados(page.results);
          setHasMore(page.hasMore);
          setRespondeu(true);
        })
        .catch((caught: unknown) => {
          if (pedido.current !== meu) return;
          setErro((caught as Error).message);
          setResultados([]);
          setHasMore(false);
        })
        .finally(() => {
          if (pedido.current === meu) setBusy(false);
        });
    }, ESPERA_MS);

    return () => window.clearTimeout(timer);
  }, [texto, canalId, autorId, serverId]);

  // O Escape fecha venha o foco de onde vier: um `onKeyDown` no painel só apanha
  // teclas com o foco lá dentro, e basta carregar num resultado e voltar atrás
  // para ele estar no body.
  useEffect(() => {
    function aoTeclar(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [onClose]);

  async function maisResultados() {
    const ultimo = resultados[resultados.length - 1];
    if (!ultimo || busy) return;
    const meu = ++pedido.current;
    setBusy(true);
    try {
      const page = await api.servers.searchMessages(serverId, {
        q: texto.trim(),
        before: ultimo.id,
        ...(canalId ? { channelId: canalId } : {}),
        ...(autorId ? { authorId: autorId } : {}),
      });
      if (pedido.current !== meu) return;
      // Acrescenta ao fim: a página seguinte é mais antiga, e o servidor já
      // devolve do mais recente para o mais antigo.
      setResultados((prev) => [...prev, ...page.results]);
      setHasMore(page.hasMore);
    } catch (caught) {
      if (pedido.current === meu) setErro((caught as Error).message);
    } finally {
      if (pedido.current === meu) setBusy(false);
    }
  }

  const termos = useMemo(() => termosDe(texto), [texto]);

  async function abrir(message: Message) {
    // Fecha-se ANTES do salto: o `jumpToMessage` espera pela janela à volta da
    // mensagem e, com o modal por cima, ficava-se a olhar para ele enquanto a
    // conversa lá atrás rolava sozinha.
    onClose();
    try {
      await state.jumpToMessage(message.channelId, message.id);
    } catch {
      // O canal pode ter sido apagado entretanto; o erro aparece na vista do
      // chat, e não vale a pena reabrir a procura só para o dizer.
    }
  }

  const poucasLetras = texto.trim().length < MINIMO;

  // O foco NÃO pode arrastar a página: a gaveta entra a deslizar e, focada no
  // meio da animação, o browser rolava a app inteira para o lado para mostrar a
  // caixa — no telemóvel a conversa desaparecia do ecrã.
  const caixaRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    caixaRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <aside className="procura" aria-label="Procurar mensagens">
      {/* A caixa É o cabeçalho: mesma altura das outras barras de topo, para a
          linha por baixo dela ficar alinhada com a do cabeçalho do canal. */}
      <div className="procura__caixa">
        <IconSearch size={16} />
        <input
          type="search"
          value={entrada}
          ref={caixaRef}
          placeholder="Procurar"
          aria-label="Texto a procurar"
          onChange={(e) => setEntrada(e.target.value)}
        />
        <button type="button" className="icon-button" onClick={onClose} title="Fechar a procura" aria-label="Fechar">
          <IconClose size={16} />
        </button>
      </div>

      {erro ? (
        <p className="procura__aviso procura__aviso--erro" role="alert">
          {erro}
        </p>
      ) : null}

      {!poucasLetras && respondeu && resultados.length > 0 ? (
        <p className="procura__conta">
          {resultados.length}
          {hasMore ? "+" : ""} {resultados.length === 1 ? "resultado" : "resultados"}
        </p>
      ) : null}

      <div className="procura__lista">
        {poucasLetras ? (
          /* O vazio do Discord é uma AJUDA, não um pedido: em vez de "escreve
             pelo menos 2 letras" (que diz o que falta e não o que se pode
             fazer), lista o que a caixa aceita. É a única maneira de alguém
             descobrir os filtros escritos. */
          <div className="procura__dicas">
            <p>Escreve para procurar em todo o servidor.</p>
            <dl>
              <div>
                <dt>de:</dt>
                <dd>
                  quem escreveu — <code>de:kawã</code>
                </dd>
              </div>
              <div>
                <dt>em:</dt>
                <dd>
                  em que canal — <code>em:geral</code>
                </dd>
              </div>
              <div>
                <dt>&quot;&nbsp;&quot;</dt>
                <dd>
                  frase exacta — <code>&quot;prazo de entrega&quot;</code>
                </dd>
              </div>
              <div>
                <dt>-</dt>
                <dd>
                  exclui uma palavra — <code>-férias</code>
                </dd>
              </div>
            </dl>
          </div>
        ) : busy && resultados.length === 0 ? (
          <p className="procura__aviso">A procurar…</p>
        ) : respondeu && resultados.length === 0 ? (
          <p className="procura__aviso">Nada encontrado. Tenta outra palavra ou tira um filtro.</p>
        ) : (
          resultados.map((message) => (
            <Resultado
              key={message.id}
              message={message}
              termos={termos}
              canal={state.channels[message.channelId]?.name ?? "canal"}
              onOpen={() => void abrir(message)}
            />
          ))
        )}

        {hasMore ? (
          <button type="button" className="procura__mais" disabled={busy} onClick={() => void maisResultados()}>
            {busy ? "A carregar…" : "Mostrar mais antigas"}
          </button>
        ) : null}
      </div>
    </aside>
  );
}

function Resultado({
  message,
  termos,
  canal,
  onOpen,
}: {
  message: Message;
  termos: readonly string[];
  canal: string;
  onOpen: () => void;
}) {
  const nome = message.member?.nickname ?? message.author.displayName ?? message.author.username;
  const texto = message.content.trim();
  // Cada fatia leva onde começa no excerto: é uma chave estável e única (duas
  // fatias nunca começam no mesmo sítio), sem cair no índice do array.
  const fatias = useMemo(() => {
    let inicio = 0;
    return realcar(excerto(texto, termos), termos).map((fatia) => {
      const comInicio = { ...fatia, inicio };
      inicio += fatia.texto.length;
      return comInicio;
    });
  }, [texto, termos]);

  return (
    <button type="button" className="procura__item" onClick={onOpen}>
      <Avatar user={message.author} overrideUrl={message.member?.avatarUrl ?? null} size={32} />
      <div className="procura__corpo">
        <div className="procura__meta">
          <strong>{nome}</strong>
          <span className="procura__canal">#{canal}</span>
          <time dateTime={message.createdAt}>{formatMessageStamp(message.createdAt)}</time>
        </div>
        {texto ? (
          // Texto simples e não `RichText`: as marcas partem o texto em pedaços
          // e o RichText precisa da string inteira para reconhecer uma menção ou
          // um bloco de código. Isto é a pré-visualização — a mensagem a sério
          // vê-se ao carregar.
          <p className="procura__texto">
            {fatias.map((fatia) =>
              fatia.realce ? (
                <mark key={fatia.inicio}>{fatia.texto}</mark>
              ) : (
                <span key={fatia.inicio}>{fatia.texto}</span>
              ),
            )}
          </p>
        ) : message.attachments.length > 0 ? (
          <p className="procura__texto procura__texto--vazio">
            {message.attachments.length === 1 ? "1 anexo" : `${message.attachments.length} anexos`}
          </p>
        ) : null}
      </div>
    </button>
  );
}
